import { defineStore } from 'pinia'
import { faucetService } from '@shared/service/faucet'
import { Wallet, isEvmBrowserWallet } from '@injectivelabs/wallet-base/light'
import { DEFAULT_BLOCK_TIMEOUT_HEIGHT } from '@injectivelabs/utils/constants'
import {
  ErrorType,
  WalletException,
  GeneralException,
  UnspecifiedErrorCode
} from '@injectivelabs/exceptions'
import {
  warmRfqClientGatewayStream,
  getRfqClientGatewayBroadcastContext
} from '@/composables/rfq/gateway/clientPrerequisites'
import { RFQ_CONTRACT_ADDRESS } from '@/app/data/rfq'
import { traceUserDetails } from '@/app/services/tracer'
import { GEO_RESTRICTED_ACTION_MESSAGE } from '@/app/data/errors'
import { TRADING_MESSAGES, RFQ_TRADING_MESSAGES } from '@/app/data/trade'
import { registerDefaultWalletCleanups } from '@/app/services/walletCleanupRegistrations'
import {
  runWalletCleanup,
  assertRequiredWalletCleanupsRegistered
} from '@/app/services/walletCleanup'
import {
  isGeoRestricted,
  isGeoRestrictionEnabled,
  isDerivativeGeoRestricted
} from '@/app/data/geoip'
import {
  getFeeGrantKey,
  isFeeGrantFresh,
  prepareFeeGrant,
  feeGrantRequests,
  getFeeGrantRequestKey
} from '@/store/wallet/helpers/feeGrant'
import { Modal, BusEvents, AutoSignSetupStatus } from '@/types'
import type { AutoSign } from '@shared/types'
import type { AutoSignSetupState } from '@/types'
import type { FeeGrant, WalletStoreState } from '@/store/wallet/helpers/types'

type GeoRestrictedPredicate = (country: string) => boolean
type GeoRestrictedOptions = {
  shouldOpenModal?: boolean
}

const AUTO_SIGN_GRANT_DURATION = 60 * 60 * 24 * 60

function normalizeMessageType(messageType: string) {
  return messageType.startsWith('/') ? messageType : `/${messageType}`
}

async function getHasMissingOrExpiringGrants() {
  const { hasMissingOrExpiringGrants } = await import(
    '@shared/wallet/utils/authz'
  )

  return hasMissingOrExpiringGrants
}

async function getMsgBroadcaster() {
  const { getMsgBroadcaster } = await import('@shared/wallet')

  return await getMsgBroadcaster()
}

async function getWalletStrategy() {
  const { getWalletStrategy } = await import('@shared/wallet')

  return await getWalletStrategy()
}

async function validateEvmWallet(
  input: Parameters<typeof import('@shared/wallet')['validateEvmWallet']>[0]
) {
  const { validateEvmWallet } = await import('@shared/wallet')

  return await validateEvmWallet(input)
}

async function switchToInjectiveEvmNetwork(wallet: Wallet) {
  const { switchToInjectiveEvmNetwork } = await import('@shared/wallet')

  return await switchToInjectiveEvmNetwork(wallet)
}

async function hasAutoSignKey(storageKey?: string) {
  const { hasAutoSignKey } = await import('@shared/wallet/autosign')

  return await hasAutoSignKey(storageKey)
}

async function clearAutoSignKey(storageKey: string) {
  const { clearAutoSignKey } = await import('@shared/wallet/autosign')

  return await clearAutoSignKey(storageKey)
}
const validateGeoRestriction = async (
  isRestricted: GeoRestrictedPredicate,
  options: GeoRestrictedOptions = {}
) => {
  const shouldOpenModal = options.shouldOpenModal ?? true
  const modalStore = useSharedModalStore()
  const sharedGeoStore = useSharedGeoStore()

  if (!isGeoRestrictionEnabled()) {
    return
  }

  // disabled until further instructions.
  // await sharedGeoStore.fetchVpnLocation()

  if (!isRestricted(sharedGeoStore.country)) {
    return
  }

  if (shouldOpenModal) {
    modalStore.openModal(Modal.GeoRestricted)
  }

  throw new GeneralException(new Error(GEO_RESTRICTED_ACTION_MESSAGE))
}

const initialAutoSignFactory = (): AutoSignSetupState => ({
  account: AutoSignSetupStatus.Pending,
  authorization: AutoSignSetupStatus.Pending
})

const initialStateFactory = (): WalletStoreState => ({
  feeGrants: {},
  autoSignSetup: initialAutoSignFactory()
})

export const useWalletStore = defineStore('wallet', {
  state: (): WalletStoreState => initialStateFactory(),
  getters: {
    isAutoSignSetupComplete: (state) => {
      return state.autoSignSetup.account === AutoSignSetupStatus.Complete
    },

    isAutoSignAuthorizationComplete: (state) => {
      return state.autoSignSetup.authorization === AutoSignSetupStatus.Complete
    },

    isAutoSignComplete: (state) => {
      return (
        state.autoSignSetup.account === AutoSignSetupStatus.Complete &&
        state.autoSignSetup.authorization === AutoSignSetupStatus.Complete
      )
    }
  },
  actions: {
    setAutoSignSetup(autoSignSetup: Partial<AutoSignSetupState>) {
      this.autoSignSetup = {
        ...this.autoSignSetup,
        ...autoSignSetup
      }
    },

    setAutoSignSetupState(autoSignSetup: AutoSignSetupState) {
      this.autoSignSetup = autoSignSetup
    },

    setFeeGrant(feeGrant: FeeGrant) {
      const key = getFeeGrantKey(feeGrant)

      this.feeGrants = {
        ...this.feeGrants,
        [key]: feeGrant
      }
    },

    getFeeGrant(params?: { granteeAddress?: string; granterAddress?: string }) {
      const sharedWalletStore = useSharedWalletStore()

      if (
        !sharedWalletStore.isUserConnected ||
        !sharedWalletStore.isAutoSignEnabled
      ) {
        return
      }

      const granteeAddress =
        params?.granteeAddress || sharedWalletStore.autoSign?.injectiveAddress

      if (!granteeAddress) {
        return
      }

      if (params?.granterAddress) {
        const feeGrant =
          this.feeGrants[
            getFeeGrantKey({
              granteeAddress,
              granterAddress: params.granterAddress
            })
          ]

        return isFeeGrantFresh(feeGrant) ? feeGrant : undefined
      }

      return Object.values(this.feeGrants).find(
        (feeGrant) =>
          feeGrant.granteeAddress === granteeAddress &&
          isFeeGrantFresh(feeGrant)
      )
    },

    async requestFeeGrant(params?: {
      forceRefresh?: boolean
      granteeAddress?: string
      granterAddress?: string
    }) {
      const walletStore = useWalletStore()
      const sharedWalletStore = useSharedWalletStore()

      if (
        !sharedWalletStore.isUserConnected ||
        !sharedWalletStore.isAutoSignEnabled
      ) {
        return
      }

      const forceRefresh = params?.forceRefresh ?? false
      const granteeAddress =
        params?.granteeAddress || sharedWalletStore.autoSign?.injectiveAddress

      if (!granteeAddress) {
        return
      }

      const cachedFeeGrant = walletStore.getFeeGrant({
        granteeAddress,
        granterAddress: params?.granterAddress
      })

      if (!forceRefresh && cachedFeeGrant) {
        return cachedFeeGrant
      }

      const feeGrantRequestKey = getFeeGrantRequestKey({
        granteeAddress,
        granterAddress: params?.granterAddress
      })

      if (feeGrantRequests[feeGrantRequestKey]) {
        return await feeGrantRequests[feeGrantRequestKey]
      }

      const request = prepareFeeGrant({ granteeAddress }).then((feeGrant) => {
        walletStore.setFeeGrant(feeGrant)

        return feeGrant
      })

      const clearRequest = () => {
        if (feeGrantRequests[feeGrantRequestKey] === request) {
          feeGrantRequests[feeGrantRequestKey] = undefined
        }
      }

      feeGrantRequests[feeGrantRequestKey] = request
      request.then(clearRequest, clearRequest)

      return await request
    },

    requestFeeGrantInBackground() {
      const sharedWalletStore = useSharedWalletStore()

      if (
        !sharedWalletStore.isUserConnected ||
        !sharedWalletStore.isAutoSignEnabled
      ) {
        return
      }

      this.requestFeeGrant().catch(() => {})
    },

    requestRfqClientGatewayFeeGrantInBackground() {
      const appStore = useAppStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!appStore.userState.preferences.isRfqClientGatewayEnabled) {
        return
      }

      this.requestFeeGrantInBackground()

      if (
        !sharedWalletStore.injectiveAddress ||
        !sharedWalletStore.autoSign?.injectiveAddress
      ) {
        return
      }

      warmRfqClientGatewayStream({
        takerAddress: sharedWalletStore.injectiveAddress
      }).catch(() => {})
      getRfqClientGatewayBroadcastContext({
        autosignAddress: sharedWalletStore.autoSign.injectiveAddress
      }).catch(() => {})
    },

    async init() {
      const accessStore = useAccessStore()
      const sharedGeoStore = useSharedGeoStore()
      const sharedWalletStore = useSharedWalletStore()

      await sharedWalletStore.init()

      if (!sharedWalletStore.wallet) {
        return
      }

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      await accessStore.fetchAccessStatus()

      await traceUserDetails({
        address: sharedWalletStore.address,
        wallet: sharedWalletStore.wallet,
        geoContinent: sharedGeoStore.geoContinent,
        geoCountry: sharedGeoStore.geoCountry,
        ipAddress: sharedGeoStore.ipAddress,
        browserCountry: sharedGeoStore.browserCountry,
        vpnDetected: sharedGeoStore.vpnDetected,
        vpnCheckedTimestamp: sharedGeoStore.vpnCheckedTimestamp
      })
    },

    async connect({
      wallet,
      address,
      derivationPath
    }: {
      wallet: Wallet
      address?: string
      derivationPath?: string
    }) {
      const walletStore = useWalletStore()
      const accountStore = useAccountStore()
      const jsonStore = useSharedJsonStore()
      const modalStore = useSharedModalStore()
      const accessStore = useAccessStore()
      const sharedWalletStore = useSharedWalletStore()
      const { $onError } = useNuxtApp()

      if (
        (
          [
            Wallet.Rabby,
            Wallet.BitGet,
            Wallet.Rainbow,
            Wallet.Phantom,
            Wallet.Metamask,
            Wallet.KeplrEvm,
            Wallet.OkxWallet,
            Wallet.TrustWallet
          ] as Wallet[]
        ).includes(wallet)
      ) {
        await sharedWalletStore.connectEvmWallet(wallet)
      }

      if (([Wallet.Leap, Wallet.Keplr] as Wallet[]).includes(wallet)) {
        await sharedWalletStore.connectCosmosWallet(wallet)
      }

      if (
        ([Wallet.Ledger, Wallet.LedgerLegacy] as Wallet[]).includes(wallet) &&
        address
      ) {
        await sharedWalletStore.connectLedger({
          wallet,
          address,
          derivationPath
        })
      }

      if (
        ([Wallet.TrezorBip32, Wallet.TrezorBip44] as Wallet[]).includes(
          wallet
        ) &&
        address
      ) {
        await sharedWalletStore.connectTrezor({
          wallet,
          address,
          derivationPath
        })
      }

      if (wallet === Wallet.WalletConnect) {
        await sharedWalletStore.connectWalletConnect()

        const msgBroadcaster = await getMsgBroadcaster()

        await msgBroadcaster.setOptions({
          txTimeout: DEFAULT_BLOCK_TIMEOUT_HEIGHT * 5
        })
      }

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      const someAddressInWalletIsBlackListed = sharedWalletStore.addresses.some(
        (address) =>
          jsonStore.blacklistedAddresses.find(
            (blacklistedAddress) =>
              blacklistedAddress.toLowerCase() === address.toLowerCase()
          ) !== undefined
      )

      if (someAddressInWalletIsBlackListed) {
        await walletStore.disconnect()

        throw new WalletException(
          new Error('Connected account address is restricted.'),
          {
            code: UnspecifiedErrorCode,
            type: ErrorType.WalletError
          }
        )
      }

      accountStore.updateSubaccount(sharedWalletStore.defaultSubaccountId || '')
      modalStore.closeModal(Modal.Connect)

      await accessStore.fetchAccessStatus()

      import('@/app/providers/mixpanel/WalletTracker')
        .then(({ trackLogin }) =>
          trackLogin({
            wallet,
            evmAddress: sharedWalletStore.address,
            address: sharedWalletStore.injectiveAddress
          })
        )
        .catch($onError)
    },

    async validateGeoRestrictedAction(options?: GeoRestrictedOptions) {
      await validateGeoRestriction(isGeoRestricted, options)
    },

    async validateDerivativeGeoRestrictedAction(
      options?: GeoRestrictedOptions
    ) {
      await validateGeoRestriction(isDerivativeGeoRestricted, options)
    },

    async validateAndFundForGas() {
      const accountStore = useAccountStore()
      const sharedWalletStore = useSharedWalletStore()

      if (sharedWalletStore.isFeeDelegationEnabled) {
        return
      }

      if (accountStore.hasSignerGasBalance) {
        return
      }

      await faucetService.fundInjectiveAddress(
        sharedWalletStore.injectiveAddress
      )
      await accountStore.fetchSignerInjBalance()

      if (accountStore.hasSignerGasBalance) {
        return
      }

      throw new GeneralException(
        new Error(
          'Due to extremely high usage, gas-free transactions are currently unavailable. You need INJ to cover the transaction.'
        )
      )
    },

    async disconnect() {
      const sharedWalletStore = useSharedWalletStore()

      if (sharedWalletStore.wallet === Wallet.WalletConnect) {
        const msgBroadcaster = await getMsgBroadcaster()

        await msgBroadcaster.setOptions({
          txTimeout: DEFAULT_BLOCK_TIMEOUT_HEIGHT
        })
      }

      registerDefaultWalletCleanups()
      assertRequiredWalletCleanupsRegistered()

      await runWalletCleanup()
    },

    async signArbitraryData(address: string, message: string) {
      const sharedWalletStore = useSharedWalletStore()
      const walletStrategy = await getWalletStrategy()

      if (sharedWalletStore.isSSOAuth) {
        return await walletStrategy.signEip712TypedData(
          message,
          sharedWalletStore.address
        )
      }

      return await walletStrategy.signArbitrary(address, message)
    },

    resetAutoSignSetup() {
      useWalletStore().setAutoSignSetupState(initialAutoSignFactory())
    },

    async syncAutoSignSetup() {
      const authZStore = useAuthZStore()
      const walletStore = useWalletStore()
      const sharedWalletStore = useSharedWalletStore()

      const autoSign = sharedWalletStore.autoSign

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      if (!autoSign || !autoSign.injectiveAddress) {
        return
      }

      const actualAutoSign = sharedWalletStore.autoSign as AutoSign

      const isAutoSignSetupComplete = await hasAutoSignKey(
        actualAutoSign.storageKey
      )
      const hasMissingOrExpiringGrants = await getHasMissingOrExpiringGrants()

      const hasMissingDirectGrants = hasMissingOrExpiringGrants({
        grants: authZStore.granterGrants,
        grantee: autoSign.injectiveAddress,
        granter: sharedWalletStore.injectiveAddress,
        messageTypes: TRADING_MESSAGES
      })
      const hasMissingRfqGrants = hasMissingOrExpiringGrants({
        grants: authZStore.granterGrants,
        grantee: RFQ_CONTRACT_ADDRESS,
        granter: sharedWalletStore.injectiveAddress,
        messageTypes: RFQ_TRADING_MESSAGES
      })

      const isAutoSignAuthorizationComplete =
        !hasMissingDirectGrants && !hasMissingRfqGrants

      sharedWalletStore.autoSign = {
        ...actualAutoSign,
        isConfirmed: isAutoSignSetupComplete
      }

      walletStore.setAutoSignSetup({
        account: isAutoSignSetupComplete
          ? AutoSignSetupStatus.Complete
          : AutoSignSetupStatus.Pending,
        authorization: isAutoSignAuthorizationComplete
          ? AutoSignSetupStatus.Complete
          : AutoSignSetupStatus.Pending
      })
    },

    async validateAutoSignSetupInBackground() {
      const authZStore = useAuthZStore()
      const walletStore = useWalletStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      if (sharedWalletStore.isReadOnly) {
        walletStore.setAutoSignSetup({
          account: AutoSignSetupStatus.Complete,
          authorization: AutoSignSetupStatus.Complete
        })

        return
      }

      await authZStore.fetchIfEmptyGrants()

      await walletStore.syncAutoSignSetup()
    },

    async setupAutoSign() {
      const walletStore = useWalletStore()
      const sharedWalletStore = useSharedWalletStore()

      try {
        await switchToInjectiveEvmNetwork(sharedWalletStore.wallet)
      } catch {
        useEventBus(BusEvents.EvmNetworkMismatch).emit()
      }

      const isAutoSignSetupComplete = await hasAutoSignKey(
        sharedWalletStore.autoSign?.storageKey
      )

      if (isAutoSignSetupComplete) {
        walletStore.setAutoSignSetup({
          account: AutoSignSetupStatus.Complete
        })

        return
      }

      try {
        walletStore.setAutoSignSetup({
          account: AutoSignSetupStatus.Loading
        })

        if (isEvmBrowserWallet(sharedWalletStore.wallet)) {
          await validateEvmWallet({
            wallet: sharedWalletStore.wallet,
            address: sharedWalletStore.address
          })
        }

        const autoSign = await sharedWalletStore.connectDeterministicAutoSign()

        sharedWalletStore.autoSign = autoSign

        walletStore.setAutoSignSetup({
          account: AutoSignSetupStatus.Complete
        })
      } catch (error) {
        walletStore.setAutoSignSetup({
          account: AutoSignSetupStatus.Failed
        })

        throw error
      }
    },

    async authorizeAutoSign() {
      const authZStore = useAuthZStore()
      const walletStore = useWalletStore()
      const sharedWalletStore = useSharedWalletStore()
      const notificationStore = useSharedNotificationStore()

      if (!sharedWalletStore.autoSign) {
        return
      }

      try {
        walletStore.setAutoSignSetup({
          authorization: AutoSignSetupStatus.Loading
        })

        if (isEvmBrowserWallet(sharedWalletStore.wallet)) {
          await validateEvmWallet({
            wallet: sharedWalletStore.wallet,
            address: sharedWalletStore.address
          })
        }

        const authorizedAutoSign =
          await sharedWalletStore.authorizeDeterministicAutoSign({
            autoSign: sharedWalletStore.autoSign,
            msgsType: TRADING_MESSAGES,
            existingGrants: authZStore.granterGrants,
            contractMsgTypeMap: {
              [RFQ_CONTRACT_ADDRESS]: RFQ_TRADING_MESSAGES
            }
          })

        sharedWalletStore.autoSign = {
          ...sharedWalletStore.autoSign,
          ...authorizedAutoSign,
          duration:
            authorizedAutoSign.duration ||
            sharedWalletStore.autoSign.duration ||
            AUTO_SIGN_GRANT_DURATION
        }

        walletStore.setAutoSignSetup({
          authorization: AutoSignSetupStatus.Complete
        })
      } catch (error) {
        walletStore.setAutoSignSetup({
          authorization: AutoSignSetupStatus.Failed
        })

        throw error
      } finally {
        if (notificationStore.notifications?.[0]?.id) {
          notificationStore.clear(notificationStore.notifications[0].id)
        }
      }

      await authZStore.fetchGrants()
    },

    async disconnectAutoSignEverywhere() {
      const authZStore = useAuthZStore()
      const sharedWalletStore = useSharedWalletStore()

      const autoSign = sharedWalletStore.autoSign

      if (!autoSign?.injectiveAddress) {
        return
      }

      await authZStore.fetchIfEmptyGrants()

      const rfqMessageTypes = RFQ_TRADING_MESSAGES.map(normalizeMessageType)
      const directGrantMessageTypes = [
        ...new Set(
          authZStore.granterGrants
            .filter((grant) => grant.grantee === autoSign.injectiveAddress)
            .map((grant) => grant.authorization?.msg)
            .filter((messageType): messageType is string => !!messageType)
        )
      ]
      const rfqGrantMessageTypes = [
        ...new Set(
          authZStore.granterGrants
            .filter(
              (grant) =>
                grant.grantee === RFQ_CONTRACT_ADDRESS &&
                rfqMessageTypes.includes(grant.authorization?.msg || '')
            )
            .map((grant) => grant.authorization?.msg)
            .filter((messageType): messageType is string => !!messageType)
        )
      ]

      await authZStore.revokeAuthorizations([
        {
          grantee: autoSign.injectiveAddress,
          messageTypes: directGrantMessageTypes
        },
        {
          grantee: RFQ_CONTRACT_ADDRESS,
          messageTypes: rfqGrantMessageTypes
        }
      ])

      if (autoSign.storageKey) {
        await clearAutoSignKey(autoSign.storageKey)
      }

      await sharedWalletStore.disconnectAutoSign()
    }
  }
})
